import classNames from "classnames";
import React, { ReactNode } from "react";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import { ImagePresetCollection, validImage } from "../../utilities/image";
import { Picture } from "./../Picture";
import { getHelpCardColorTheme, HelpCardColorTheme } from "./HelpCard";

export interface Props {
  children?: ReactNode;
  className?: string;
  colorTheme?: HelpCardColorTheme;
  innerAdditionalClassName?: string;
  outsidePadding?: boolean;
  rightAligned?: boolean;
  roundedClassName?: string;
  splitCards?: boolean;
  wideImage?: boolean;

  // Sanity-schema sourced props
  backgroundImage?: SanityImageSourceExt;
  disableImageFilters?: boolean;
}

const HelpCardContainer: React.FC<Props> = ({
  backgroundImage,
  children,
  className,
  colorTheme,
  disableImageFilters = false,
  innerAdditionalClassName,
  outsidePadding = true,
  rightAligned,
  roundedClassName = "",
  splitCards,
  wideImage
}) => {
  let alignmentCss: string;

  const imagePresets: ImagePresetCollection = wideImage
    ? {
        default: {
          // up to width 767px
          width: 714
        },
        md: {
          // from width 768px
          width: 955
        },
        lg: {
          // from width 1024px
          width: 1233
        }
      }
    : {
        default: {
          // up to width 767px
          width: 714
        },
        md: {
          // from width 768px
          width: 647
        },
        lg: {
          // from width 1024px
          width: 747
        }
      };

  if (rightAligned) {
    alignmentCss = classNames({
      "mx-4 md:mr-0 md:ml-8 lg:ml-16 lg:mb-0": splitCards && outsidePadding,
      "sm:ml-16 lg:mb-0": splitCards && !outsidePadding,
      "mx-4 md:mr-0 md:-ml-16 md:-mt-16 md:mb-16": !splitCards && outsidePadding,
      "md:-ml-16 md:-mt-16 md:mb-16": !splitCards && !outsidePadding
    });
  } else {
    alignmentCss = classNames({
      "mx-4 md:mx-0 lg:mb-0": outsidePadding,
      "lg:mb-0": !outsidePadding
    });
  }

  const {
    name: themeName,
    backgroundColor,
    borderColor
  } = getHelpCardColorTheme(colorTheme ?? HelpCardColorTheme.Default);

  const hasValidImage = validImage(backgroundImage);

  const helpCardClassNames = classNames(
    "relative shadow-card p-4 lg:px-16 lg:py-12 mb-4 flex flex-col flex-1 bg-cover",
    alignmentCss,
    backgroundColor,
    className,
    roundedClassName,
    themeName,
    {
      [`border ${borderColor}`]: !hasValidImage
    }
  );

  return (
    <div className={helpCardClassNames}>
      {hasValidImage && (
        <Picture
          className={classNames("absolute top-0 left-0 w-full h-full z-0", {
            "opacity-40": !disableImageFilters
          })}
          image={backgroundImage}
          imgClassName={classNames(roundedClassName, "w-full h-full object-cover")}
          presets={imagePresets}
          imgLoading="lazy"
        />
      )}
      <div
        className={classNames(
          "relative z-10 flex flex-col flex-1",
          innerAdditionalClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default HelpCardContainer;

